import React from 'react';
import { Clock, Sunrise, Sunset, Moon } from 'lucide-react';

/**
 * WorkShiftsField — 3 checkboxes (Matutino/Vespertino/Nocturno), each with hora_inicio/hora_fin.
 *
 * value shape: [{ turno: 'matutino'|'vespertino'|'nocturno', hora_inicio: 'HH:MM', hora_fin: 'HH:MM' }, ...]
 * onChange receives the same shape.
 *
 * Accepts legacy string values (from old free-text field) and displays them as a read-only note
 * plus an "Actualizar" button that switches to structured mode.
 */

const DEFAULT_HOURS = {
    matutino: { hora_inicio: '07:00', hora_fin: '15:00' },
    vespertino: { hora_inicio: '15:00', hora_fin: '23:00' },
    nocturno: { hora_inicio: '23:00', hora_fin: '07:00' },
};

const SHIFT_LABELS = {
    matutino: { label: 'Turno Matutino', icon: Sunrise, color: '#F59E0B' },
    vespertino: { label: 'Turno Vespertino', icon: Sunset, color: '#F97316' },
    nocturno: { label: 'Turno Nocturno', icon: Moon, color: '#6366F1' },
};

function normalize(value) {
    // Convert legacy string (or null) to [] so UI has a starting point
    if (!value) return [];
    if (Array.isArray(value)) return value;
    return []; // legacy string — treat as empty structured list; caller may show legacy
}

export default function WorkShiftsField({ value, onChange, disabled = false, testIdPrefix = 'workshift' }) {
    const shifts = normalize(value);
    const activeMap = shifts.reduce((acc, s) => ({ ...acc, [s.turno]: s }), {});
    const legacyString = typeof value === 'string' && value.trim() ? value.trim() : null;

    const toggle = (turno) => {
        if (disabled) return;
        if (activeMap[turno]) {
            onChange(shifts.filter((s) => s.turno !== turno));
        } else {
            const next = [...shifts, { turno, ...DEFAULT_HOURS[turno] }];
            // Keep order matutino > vespertino > nocturno
            const order = { matutino: 0, vespertino: 1, nocturno: 2 };
            next.sort((a, b) => (order[a.turno] || 99) - (order[b.turno] || 99));
            onChange(next);
        }
    };

    const setHour = (turno, key, val) => {
        if (disabled) return;
        onChange(shifts.map((s) => (s.turno === turno ? { ...s, [key]: val } : s)));
    };

    return (
        <div>
            <label className="text-xs uppercase tracking-wider font-bold mb-2 block text-gray-500">
                Turnos laborales
            </label>
            {legacyString && (
                <div className="mb-2 px-3 py-2 border border-amber-200 bg-amber-50 text-amber-900 text-xs">
                    <b>Registro anterior:</b> {legacyString}
                    <div className="text-[10px] mt-1 opacity-80">
                        Actualiza marcando los turnos abajo para migrar al formato estructurado.
                    </div>
                </div>
            )}
            <div className="space-y-2">
                {['matutino', 'vespertino', 'nocturno'].map((turno) => {
                    const active = !!activeMap[turno];
                    const cfg = SHIFT_LABELS[turno];
                    const Icon = cfg.icon;
                    const s = activeMap[turno] || {};
                    return (
                        <div key={turno} className="border" style={{ borderColor: 'var(--border, #E5E7EB)' }}
                             data-testid={`${testIdPrefix}-${turno}`}>
                            <label className="flex items-center gap-3 px-3 py-2 cursor-pointer hover:bg-black/5 dark:hover:bg-white/5">
                                <input
                                    type="checkbox"
                                    checked={active}
                                    onChange={() => toggle(turno)}
                                    disabled={disabled}
                                    data-testid={`${testIdPrefix}-${turno}-check`}
                                    className="w-4 h-4"
                                />
                                <Icon className="w-4 h-4" style={{ color: cfg.color }} />
                                <span className="font-medium text-sm">{cfg.label}</span>
                            </label>
                            {active && (
                                <div className="grid grid-cols-2 gap-2 px-3 pb-3 pt-1 border-t" style={{ borderColor: 'var(--border, #E5E7EB)' }}>
                                    <div>
                                        <div className="text-[10px] uppercase tracking-wider font-bold text-gray-500 mb-1">Hora inicio</div>
                                        <input
                                            type="time"
                                            value={s.hora_inicio || ''}
                                            onChange={(e) => setHour(turno, 'hora_inicio', e.target.value)}
                                            disabled={disabled}
                                            data-testid={`${testIdPrefix}-${turno}-inicio`}
                                            className="w-full px-2 py-1.5 border text-sm outline-none"
                                            style={{ borderColor: 'var(--border, #E5E7EB)' }}
                                        />
                                    </div>
                                    <div>
                                        <div className="text-[10px] uppercase tracking-wider font-bold text-gray-500 mb-1">Hora fin</div>
                                        <input
                                            type="time"
                                            value={s.hora_fin || ''}
                                            onChange={(e) => setHour(turno, 'hora_fin', e.target.value)}
                                            disabled={disabled}
                                            data-testid={`${testIdPrefix}-${turno}-fin`}
                                            className="w-full px-2 py-1.5 border text-sm outline-none"
                                            style={{ borderColor: 'var(--border, #E5E7EB)' }}
                                        />
                                    </div>
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>
        </div>
    );
}

/**
 * Render helper for read-only display of work_shifts.
 * Accepts array (new) or string (legacy) and returns JSX.
 */
export function WorkShiftsSummary({ value }) {
    if (!value) return null;
    if (typeof value === 'string') {
        return <span>{value}</span>;
    }
    if (!Array.isArray(value) || value.length === 0) return null;
    return (
        <div className="flex flex-wrap gap-1.5">
            {value.map((s) => {
                const cfg = SHIFT_LABELS[s.turno] || { label: s.turno, color: '#6B7280', icon: Clock };
                const Icon = cfg.icon;
                return (
                    <span
                        key={s.turno}
                        className="inline-flex items-center gap-1 px-2 py-0.5 text-[11px] font-medium border"
                        style={{ borderColor: `${cfg.color}40`, background: `${cfg.color}12`, color: cfg.color }}
                    >
                        <Icon className="w-3 h-3" />
                        {cfg.label.replace('Turno ', '')} {s.hora_inicio || '?'}–{s.hora_fin || '?'}
                    </span>
                );
            })}
        </div>
    );
}
